










































































































































import {Component, Prop, Vue} from 'vue-property-decorator';
import SubscriptionPlanService from '@/services/SubscriptionPlanService';
import CompanyService from '@/services/CompanyService';
import PersonService from '@/services/PersonService';
import {ChargeComponent} from "@/dto/subscription/SublimeChargeDTO";
import PortalAddress from "@/components/profile/branches/PortalAddress.vue";
import PayPalPlanDTO from "@/dto/subscription/PayPalPlanDTO";
import {processError} from "@/utils/ComponentUtils";
import Notifications from "@/state/Notifications";
import Workspace, {WorkspaceType} from "@/dto/auth/Workspace";
import {DiscountActionType, DiscountScope, SubscriberType} from "@/constants/SubscriptionConstants";

@Component({
  components: {
    PortalAddress
  }
})
export default class SubscriptionOffer extends Vue {
  @Prop({ required: false }) groupName?: string;
  @Prop({ required: true }) offerHeader!: string;

  private subscriptionPlans: PayPalPlanDTO[] = [];
  private salesTaxRate: number | null = null;

  // Unified list: person workspace (if exists) + company workspaces
  private allWorkspaces: Array<Workspace> = []
  private selectedWorkspaces: Array<Workspace> = []
  
  // Mobile periodicity selector (default to ANNUALLY)
  private selectedPeriodicity = 'ANNUALLY';
  
  // Track if we're in mobile view
  private isMobileView = false;

  async mounted() {
    // Check initial window size
    this.checkMobileView();
    
    // Add resize listener
    window.addEventListener('resize', this.checkMobileView);
    // Get person and company workspaces from $wss
    const personWorkspaces = this.$wss.getAccessibleWorkspaces().filter(ws => this.$wss.isOwnWorkspace(ws) && ws.type == WorkspaceType.PERSON)
    const companyWorkspaces = this.$wss.getAccessibleWorkspaces().filter(ws => this.$wss.isOwnWorkspace(ws) && ws.type == WorkspaceType.COMPANY)
    
    // Combine: person first, then companies
    this.allWorkspaces = [...personWorkspaces, ...companyWorkspaces]
    
    // Initialize with all workspaces selected
    this.selectedWorkspaces = [...this.allWorkspaces];
    await this.loadData();
  }

  beforeDestroy() {
    // Remove resize listener
    window.removeEventListener('resize', this.checkMobileView);
  }

  private checkMobileView() {
    this.isMobileView = window.innerWidth <= 768;
  }

  loadSalesTax(){
    const personWorkspace = this.allWorkspaces.find(ws => ws.type === WorkspaceType.PERSON)
    const companyWorkspace = this.allWorkspaces.find(ws => ws.type === WorkspaceType.COMPANY)
    
    if(!personWorkspace && !companyWorkspace) {
      this.salesTaxRate = 0;
      return Promise.resolve();
    }
    
    this.$loadOn()

    // Decide priority based on subscriber type
    const wantPersonFirst = this.currentSubscriberType === SubscriberType.PERSON;
    const wantCompanyFirst = this.currentSubscriberType === SubscriberType.COMPANY;

    const tryPerson = () => personWorkspace
      ? PersonService.getSalesTaxRate(personWorkspace.id).then(
          ok => { this.$loadOff(); this.salesTaxRate = ok.data.rate; return Promise.resolve(ok); },
          err => { this.$loadOff(); processError(err, this); return Promise.reject(err); }
        )
      : null;

    const tryCompany = () => companyWorkspace
      ? CompanyService.getSalesTaxRate(companyWorkspace.id).then(
          ok => { this.$loadOff(); this.salesTaxRate = ok.data.rate; return Promise.resolve(ok); },
          err => { this.$loadOff(); processError(err, this); return Promise.reject(err); }
        )
      : null;

    if (wantPersonFirst) {
      return tryPerson() || tryCompany() || Promise.resolve();
    } else if (wantCompanyFirst) {
      return tryCompany() || tryPerson() || Promise.resolve();
    } else {
      // Default: prefer person, then company
      return tryPerson() || tryCompany() || Promise.resolve();
    }
  }

  loadSubscriptionPlans() {
    this.$loadOn();
    return SubscriptionPlanService.getActivePayPalPlans(this.groupName).then(
        ok => {
          this.$loadOff();
          this.subscriptionPlans = ok.data;
          Promise.resolve(ok);
        },
        err => {
          processError(err, this);
          Promise.reject(err);
        }
    )
  }

  private async loadData() {
    await this.loadSubscriptionPlans()
    // After plans are loaded, reset selections to match allowed subscriber type
    this.selectedWorkspaces = [...this.filteredWorkspaces];
    await this.loadSalesTax()
    if(this.subscriptionPlans.length < 1){
      Notifications.warning("Subscription for selected service is temporarily unavailable");
    }
  }

  private formatPrice(priceCents: number | null): string {
    if (priceCents === null) {
      return '0.00';
    }
    return (priceCents / 100).toFixed(2);
  }

  private formatTaxRate(rate: number): string {
    return (rate * 100).toFixed(3);
  }

  private isWorkspaceSelected(workspace: Workspace): boolean {
    return this.selectedWorkspaces.some(ws => ws.id === workspace.id && ws.type === workspace.type);
  }

  private toggleWorkspace(workspace: Workspace): void {
    const index = this.selectedWorkspaces.findIndex(ws => ws.id === workspace.id && ws.type === workspace.type);
    if (index >= 0) {
      this.selectedWorkspaces.splice(index, 1);
    } else {
      this.selectedWorkspaces.push(workspace);
    }
  }

  private getSelectedProfilesCount(): number {
    return this.selectedWorkspaces.length;
  }

  // Computed property: get list of available periodicities from loaded plans
  get availablePeriodicities(): string[] {
    return this.subscriptionPlans.map(plan => plan.periodicity);
  }

  // Computed property: filter plans for mobile view (show only selected), desktop shows all
  get visiblePlans(): PayPalPlanDTO[] {
    if (this.isMobileView) {
      // On mobile, show only the selected periodicity
      return this.subscriptionPlans.filter(plan => plan.periodicity === this.selectedPeriodicity);
    }
    
    // On desktop, show all plans
    return this.subscriptionPlans;
  }

  // Whether any of the currently visible plans has per-profile discount affecting price
  get hasDiscountForSelector(): boolean {
    return this.visiblePlans.some(p => this.hasDiscount(p));
  }

  // Determine current subscriber type from loaded plans
  get currentSubscriberType(): SubscriberType {
    if (this.subscriptionPlans.length === 0) {
      return SubscriberType.ALL;
    }
    const first = this.subscriptionPlans[0].subscriberType;
    const mixed = this.subscriptionPlans.some(p => p.subscriberType !== first);
    return mixed ? SubscriberType.ALL : first;
  }

  // Workspaces filtered according to subscriber type
  get filteredWorkspaces(): Array<Workspace> {
    switch (this.currentSubscriberType) {
      case SubscriberType.COMPANY:
        return this.allWorkspaces.filter(ws => ws.type === WorkspaceType.COMPANY);
      case SubscriberType.PERSON:
        return this.allWorkspaces.filter(ws => ws.type === WorkspaceType.PERSON);
      default:
        return this.allWorkspaces;
    }
  }

  private hasDiscount(plan: PayPalPlanDTO): boolean {
    return plan.discounts && plan.discounts.length > 0 && 
           plan.discounts.some(d => 
             d.scope === DiscountScope.EACH_CONSOLIDATED_OWNER && 
             d.actionType === DiscountActionType.FIXED_UNIT_PRICE_CENTS
           );
  }

  private getDiscountedPrice(plan: PayPalPlanDTO): number | null {
    if (!this.hasDiscount(plan)) {
      return plan.currentUnitPriceCents;
    }
    const discount = plan.discounts.find(d => 
      d.scope === DiscountScope.EACH_CONSOLIDATED_OWNER && 
      d.actionType === DiscountActionType.FIXED_UNIT_PRICE_CENTS
    );
    return discount ? discount.actionValue : plan.currentUnitPriceCents;
  }

  private calculateSubtotalWithoutDiscount(plan: PayPalPlanDTO): number | null {
    if (plan.currentUnitPriceCents === null) {
      return null;
    }
    const profilesCount = this.getSelectedProfilesCount();
    return plan.currentUnitPriceCents * profilesCount;
  }

  private calculateSubtotal(plan: PayPalPlanDTO): number | null {
    if (plan.currentUnitPriceCents === null) {
      return null;
    }
    
    const profilesCount = this.getSelectedProfilesCount();
    
    // If no discount, price does not depend on number of profiles
    if (!this.hasDiscount(plan)) {
      return plan.currentUnitPriceCents;
    }

    // If only one profile, use base price
    if (profilesCount <= 1) {
      return plan.currentUnitPriceCents;
    }
    
    // With discount: base price for first profile + discounted price for additional profiles
    const discountedPrice = this.getDiscountedPrice(plan);
    if (discountedPrice === null) {
      return null;
    }
    
    return plan.currentUnitPriceCents + (discountedPrice * (profilesCount - 1));
  }

  private calculateSalesTax(plan: PayPalPlanDTO): number | null {
    const subtotal = this.calculateSubtotal(plan);
    if (subtotal === null || this.salesTaxRate === null) {
      return null;
    }
    return Math.round(subtotal * this.salesTaxRate);
  }

  private calculateTotalWithTax(plan: PayPalPlanDTO): number | null {
    const subtotal = this.calculateSubtotal(plan);
    const salesTax = this.calculateSalesTax(plan);
    if (subtotal === null || salesTax === null) {
      return null;
    }
    return subtotal + salesTax;
  }

  private isNonMonthlyPeriod(plan: PayPalPlanDTO): boolean {
    return plan.periodicity !== 'MONTHLY';
  }

  private getPeriodicityMonths(plan: PayPalPlanDTO): number {
    switch (plan.periodicity) {
      case 'MONTHLY':
        return 1;
      case 'QUARTERLY':
        return 3;
      case 'SEASONLY':
        return 6;
      case 'ANNUALLY':
        return 12;
      default:
        return 1;
    }
  }

  private calculateAverageMonthly(plan: PayPalPlanDTO): number | null {
    const totalWithTax = this.calculateTotalWithTax(plan);
    if (totalWithTax === null) {
      return null;
    }
    const months = this.getPeriodicityMonths(plan);
    return Math.round(totalWithTax / months);
  }

  private formatPeriodicity(periodicity: string): string {
    switch (periodicity) {
      case 'MONTHLY':
        return 'Billed monthly';
      case 'QUARTERLY':
        return 'Billed quarterly';
      case 'SEASONLY':
        return 'Billed semi-annually';
      case 'ANNUALLY':
        return 'Billed annually';
      default:
        return periodicity;
    }
  }

  private formatSubscribeButtonText(periodicity: string): string {
    switch (periodicity) {
      case 'MONTHLY':
        return 'Subscribe monthly';
      case 'QUARTERLY':
        return 'Subscribe quarterly';
      case 'SEASONLY':
        return 'Subscribe semi-annually';
      case 'ANNUALLY':
        return 'Subscribe annually';
      default:
        return 'Subscribe now';
    }
  }

  private formatSubscriptionTitle(periodicity: string): string {
    switch (periodicity) {
      case 'MONTHLY':
        return 'Monthly subscription';
      case 'QUARTERLY':
        return 'Quarterly subscription';
      case 'SEASONLY':
        return 'Semi-annual subscription';
      case 'ANNUALLY':
        return 'Annual subscription';
      default:
        return 'Subscription';
    }
  }

  private formatTotalLabel(periodicity: string): string {
    switch (periodicity) {
      case 'MONTHLY':
        return 'Total per month:';
      case 'QUARTERLY':
        return 'Total per quarter:';
      case 'SEASONLY':
        return 'Total per 6 months:';
      case 'ANNUALLY':
        return 'Total per year:';
      default:
        return 'Total:';
    }
  }

  private formatPeriodicityShort(periodicity: string): string {
    switch (periodicity) {
      case 'MONTHLY':
        return 'Month';
      case 'QUARTERLY':
        return 'Quarter';
      case 'SEASONLY':
        return 'Season';
      case 'ANNUALLY':
        return 'Annual';
      default:
        return periodicity;
    }
  }

  private handleSubscribe(plan: PayPalPlanDTO) {
    // Extract person ID (if selected)
    const selectedPerson = this.selectedWorkspaces.find(ws => ws.type === WorkspaceType.PERSON);
    const personId = selectedPerson ? selectedPerson.id : null;
    
    // Extract company IDs
    const companyIds = this.selectedWorkspaces
      .filter(ws => ws.type === WorkspaceType.COMPANY)
      .map(ws => ws.id);
    
    this.$emit('subscribe', plan.id, companyIds, personId);
  }
}
